import Highcharts from 'highcharts';

// Since v12 modules register themselves on the Highcharts namespace on import, with no factory call.
import 'highcharts/modules/stock';
import 'highcharts/modules/map';
import 'highcharts/highcharts-more';
import 'highcharts/modules/no-data-to-display';
import 'highcharts/modules/marker-clusters';
import 'highcharts/modules/variable-pie';
import 'highcharts/modules/solid-gauge';
import 'highcharts/modules/exporting';
import 'highcharts/modules/sankey';
import 'highcharts/modules/timeline';
import 'highcharts/modules/pattern-fill';

import { forEach } from 'lodash-es';

import DefaultOptions from './DefaultOptions';

const initCrossHairPlugin = (H) => {
  H.wrap(H.Axis.prototype, 'drawCrosshair', function proceedHandler(proceed, ...args) {
    const axis = this;
    const wasRendered = !!axis.cross;
    proceed.apply(axis, args);

    if (!wasRendered && axis.cross && axis.options.clickOnCrosshair) {
      axis.cross
        .on('click', (e) => axis.options.clickOnCrosshair(e))
        .css({ pointerEvents: 'auto' });
    } else if (axis.cross) {
      axis.cross.css({ pointerEvents: 'auto' });
    }
  });
};

const initShowMarkerOnSinglePoint = (H) => {
  H.wrap(H.Series.prototype, 'drawPoints', function onPointDraw(proceed) {
    const markerState = this.options.marker.enabled;
    if (markerState === true) {
      proceed.call(this);
      return;
    }
    const { points } = this;
    this.options.marker.enabled = true;
    if (points.length === 1) {
      points[0].marker = { enabled: true };
      proceed.call(this);
      this.options.marker.enabled = markerState;
      return;
    }

    forEach(points, (point, i) => {
      const showMarker =
        !point.isNull &&
        ((points[i - 1] && points[i - 1].isNull) || !points[i - 1]) &&
        ((points[i + 1] && points[i + 1].isNull) || !points[i + 1]);

      const newPoint = point;
      if (point.marker) {
        newPoint.marker.enabled = showMarker;
      } else {
        newPoint.marker = {
          enabled: showMarker,
        };
      }
      return newPoint;
    });

    proceed.call(this);

    this.options.marker.enabled = markerState;
  });
};

// Highcharts 12 fills resolve to nothing. The size is explicit:
// the package tiled at 10x10, the native default is 32x32
// to make filled patterns column and area chart work.

const initDefsPatterns = (H) => {
  H.addEvent(H.Chart, 'afterGetContainer', function onAfterGetContainer() {
    const patterns = this.options.defs?.patterns;
    if (!patterns?.length) {
      return;
    }
    const patternTileSize = 10;

    forEach(patterns, (pattern) =>
      this.renderer.addPattern(
        { width: patternTileSize, height: patternTileSize, ...pattern },
        false
      )
    );
  });
};

// Since v13 point events only fire inside the plot area, and the insights timeline sits below it.
const initFlagsOutsidePlotInteraction = (H) => {
  const flagsSeries = H.seriesTypes.flags;
  flagsSeries.prototype.allowOutsidePlotInteraction = true;
};

const initNavigatorHandlesPlacement = (H) => {
  H.wrap(H.Navigator.prototype, 'drawHandle', function onDrawHandle(proceed, x, index, inverted) {
    proceed.call(this, x, index, inverted, 'attr');

    if (this.navigatorOptions.handles.customPlacement) {
      this.handles[index].attr(this.navigatorOptions.handles.customPlacement[index]);
    }
  });
};

// Waterfall `isSum` points take the running sum of the steps; a `sumValue` point option draws the
// given total instead (non-stacked series only). Since v12 series data lives in `dataTable` columns.
const initWaterfallSumValue = (H) => {
  H.wrap(
    H.seriesTypes.waterfall.prototype,
    'processData',
    function onProcessData(proceed, ...args) {
      const result = proceed.apply(this, args);

      if (!this.options.stacking) {
        const yData = this.getColumn('y');
        const processedYData = this.getColumn('y', true);
        const cropStart = this.cropStart || 0;

        (this.options.data || []).forEach((point, i) => {
          if (!point?.isSum || !H.isNumber(point.sumValue)) return;

          yData[i] = point.sumValue;
          if (processedYData !== yData) {
            processedYData[i - cropStart] = point.sumValue;
          }
          this.dataMin = Math.min(this.dataMin, point.sumValue);
          this.dataMax = Math.max(this.dataMax, point.sumValue);
        });
      }

      return result;
    }
  );
};

Highcharts.setOptions(DefaultOptions);
initWaterfallSumValue(Highcharts);
initCrossHairPlugin(Highcharts);
initShowMarkerOnSinglePoint(Highcharts);
initNavigatorHandlesPlacement(Highcharts);
initFlagsOutsidePlotInteraction(Highcharts);
initDefsPatterns(Highcharts);

Highcharts.Color.useColorMix = false;
Highcharts.AST.allowedAttributes.push('data-points', 'data-target-id');

// Kept as a function so callers stay unchanged: every chart type now comes from the same namespace,
// and the flavour is selected by the `constructorType` prop (`chart`, `stockChart`, `mapChart`).
export default () => Highcharts;
